import { userConfigAtom } from "@portal/store/userConfig/userConfig";
import {
  CONTACT_PRIVILEGES,
  CONTACT_PRIVILEGE_MODULE,
  CURRENT_USER_PRIVILEGES,
  DISPUTE_PRIVILEGES,
  DISPUTE_PRIVILEGE_MODULE,
  DOCUMENT_PRIVILEGES,
  DOCUMENT_PRIVILEGE_MODULE,
  PTP_PRIVILEGES,
  PTP_PRIVILEGE_MODULE,
  USER_PRIVILEGE_MODULE,
} from "@portal/types/entities/privileges/privileges";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import {
  CF_PRIVILEGE_MODULE,
  CUSTOM_FIELD_PRIVILEGES,
} from "./../types/entities/privileges/privileges";

function usePrivileges() {
  const userPrivileges =
    useAtomValue(userConfigAtom).user_details?.user.privileges;

  const current_user = useAtomValue(userConfigAtom).user_details?.user.id;
  function disputePrivilegesPredicate(key: DISPUTE_PRIVILEGES) {
    const module: DISPUTE_PRIVILEGE_MODULE = {
      DISPUTE_CREATE: Boolean(userPrivileges?.includes("DISPUTE_CREATE")),
      DISPUTE_DELETE: Boolean(userPrivileges?.includes("DISPUTE_DELETE")),
      DISPUTE_DETAILED_VIEW: Boolean(
        userPrivileges?.includes("DISPUTE_DETAILED_VIEW")
      ),
      DISPUTE_LIST_VIEW: Boolean(userPrivileges?.includes("DISPUTE_LIST_VIEW")),
      DISPUTE_UPDATE: Boolean(userPrivileges?.includes("DISPUTE_UPDATE")),
    };
    return module[key];
  }

  function ptpPrivilegesPredicate(key: PTP_PRIVILEGES) {
    const module: PTP_PRIVILEGE_MODULE = {
      PTP_CREATE: Boolean(userPrivileges?.includes("PTP_CREATE")),
      PTP_DELETE: Boolean(userPrivileges?.includes("PTP_DELETE")),
      PTP_DETAILED_VIEW: Boolean(userPrivileges?.includes("PTP_DETAILED_VIEW")),
      PTP_LIST_VIEW: Boolean(userPrivileges?.includes("PTP_LIST_VIEW")),
      PTP_UPDATE: Boolean(userPrivileges?.includes("PTP_UPDATE")),
    };
    return module[key];
  }

  function contactPrivilegesPredicate(key: CONTACT_PRIVILEGES) {
    const module: CONTACT_PRIVILEGE_MODULE = {
      CONTACT_CREATE: Boolean(userPrivileges?.includes("CONTACT_CREATE")),
      CONTACT_DELETE: Boolean(userPrivileges?.includes("CONTACT_DELETE")),
      CONTACT_DETAILED_VIEW: Boolean(
        userPrivileges?.includes("CONTACT_DETAILED_VIEW")
      ),
      CONTACT_LIST_VIEW: Boolean(userPrivileges?.includes("CONTACT_LIST_VIEW")),
      CONTACT_UPDATE: Boolean(userPrivileges?.includes("CONTACT_UPDATE")),
    };
    return module[key];
  }

  function userPrivilegePredicate(key: CURRENT_USER_PRIVILEGES) {
    const module: USER_PRIVILEGE_MODULE = {
      CURRENT_USER: Boolean(userPrivileges?.includes("CURRENT_USER")),
      DOWNLOAD_SOA: Boolean(userPrivileges?.includes("DOWNLOAD_SOA")),
    };

    return module[key];
  }

  function documentPrivilegePredicate(key: DOCUMENT_PRIVILEGES) {
    const module: DOCUMENT_PRIVILEGE_MODULE = {
      DOCUMENT_CREATE: Boolean(userPrivileges?.includes("DOCUMENT_CREATE")),
      DOCUMENT_DELETE: Boolean(userPrivileges?.includes("DOCUMENT_DELETE")),
      DOCUMENT_DETAILED_VIEW: Boolean(
        userPrivileges?.includes("DOCUMENT_DETAILED_VIEW")
      ),
      DOCUMENT_LIST_VIEW: Boolean(
        userPrivileges?.includes("DOCUMENT_LIST_VIEW")
      ),
      DOCUMENT_UPDATE: Boolean(userPrivileges?.includes("DOCUMENT_UPDATE")),
    };
    return module[key];
  }
  function customFieldPrivilege(key: CUSTOM_FIELD_PRIVILEGES) {
    const module: CF_PRIVILEGE_MODULE = {
      CUSTOM_FIELD_LIST_VIEW: Boolean(
        userPrivileges?.includes("CUSTOM_FIELD_LIST_VIEW")
      ),
    };
    return module[key];
  }

  const getDisputePrivilege = useCallback(disputePrivilegesPredicate, [
    userPrivileges,
  ]);
  const getPTPPrivilege = useCallback(ptpPrivilegesPredicate, [userPrivileges]);

  const getContactPrivilege = useCallback(contactPrivilegesPredicate, [
    userPrivileges,
  ]);

  const getUserPrivilege = useCallback(userPrivilegePredicate, [
    userPrivileges,
  ]);
  const getDocumentPrivileges = useCallback(documentPrivilegePredicate, [
    userPrivileges,
  ]);
  const getCFPrivileges = useCallback(customFieldPrivilege, [userPrivileges]);

  return {
    getDisputePrivilege,
    getPTPPrivilege,
    getContactPrivilege,
    getUserPrivilege,
    getDocumentPrivileges,
    getCFPrivileges,
    current_user,
  };
}

export { usePrivileges };
